import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import PeopleIcon from '@mui/icons-material/PeopleAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { fetchUsers } from '../services/userService';
import { useOrganization } from '../contexts/OrganizationContext';
import { skillsCategories } from '../config/skillsConfig';
import { getSkillLabel } from '../utils/skillsUtils';
import { getSubscriptionByGuid } from '../services/subscriptionService';
import { CAPABILITIES, hasCapability } from '../services/capabilitiesService';

import {
    Box,
    Typography,
    TextField,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    MenuItem,
    IconButton,
    Collapse,
    Tooltip,
    Card,
    CardContent,
    LinearProgress,
    Alert,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';

const PersonnelSection = forwardRef(({ missionData, handleFieldChange, setMissionData, setIsDirty }, ref) => {
    const { selectedOrg, updateSelectedOrg } = useOrganization();
    const [subscription, setSubscription] = useState(null);
    const [personnelData, setPersonnelData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [isExpanded, setIsExpanded] = useState(true);
    const [errors, setErrors] = useState({});
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const allSkills = skillsCategories.flatMap((category) =>
        category.skills.map((skill) => skill.key)
    );
    const remotePicEligiblePersonnel = personnelData.filter(
        (person) => person.skills?.operational?.includes('remote_pic')
    );
    const skillsDropdownPersonnel = personnelData.filter(
        (person) => person.userGuid !== missionData?.picGuid
    );

    const isValidUserGuid = personnelData.some((person) => person.userGuid === missionData?.userGuid);
    const isValidPicGuid = remotePicEligiblePersonnel.some((person) => person.userGuid === missionData?.picGuid);

    const assignedToValue = isValidUserGuid ? missionData?.userGuid : '';
    const remotePicValue = isValidPicGuid ? missionData?.picGuid : '';

    const setCrew = (newCrew) => {
        // Set to null if no crew members are provided
        const crewValue = !newCrew || newCrew.length === 0 ? null : { additionalCrew: newCrew };

        // Mark as dirty for form tracking
        setIsDirty(true);

        // Update the `crew` field in missionData
        updateMissionField('crew', crewValue);
    };

    const loadPersonnelData = async () => {
        if (!selectedOrg) {
            setError('No organization selected.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = await fetchUsers(selectedOrg);
            // TODO: 
            //const filteredData = data.filter(user => user.certificateType === 1);
            setPersonnelData(data);
        } catch (err) {
            setError('Failed to load personnel data.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadSubscriptionData = async () => {
            if (selectedOrg) {
                try {
                    const subscriptionData = await getSubscriptionByGuid(selectedOrg);
                    if (subscriptionData) {
                        setSubscription(subscriptionData.subscription);
                    } else {
                        throw new Error('Subscription not found');
                    }
                } catch (error) {
                    console.error('Error loading subscription data:', error);
                    setError('Failed to load subscription data.');
                }
            } else {
                setSubscription(null); // Clear subscription if no subscriptionGuid exists
            }
        };

        loadSubscriptionData();
    }, [selectedOrg]);

    useEffect(() => {
        setIsExpanded(!missionData?.completedAt);
    }, [missionData]);

    useEffect(() => {
        loadPersonnelData();
    }, [selectedOrg]);

    // Validation logic for the section
    const validate = () => {
        const sectionErrors = {};
        if (!missionData?.userGuid) {
            sectionErrors.userGuid = 'Remote PIC is required.';
            setIsExpanded(true);
        }
        setErrors(sectionErrors);
        return sectionErrors;
    };

    useImperativeHandle(ref, () => ({
        validate,
    }));

    const handleAssignedUserChange = (userGuid) => {
        setIsDirty(true);

        // Check if picGuid is null and set it to userGuid
        const updatedPicGuid = !missionData.picGuid ? userGuid : missionData.picGuid;
        handleRemotePilotChange(updatedPicGuid);

        // Use the parent's handleFieldChange
        handleFieldChange({ target: { name: 'userGuid', value: userGuid } });

        setErrors((prevErrors) => ({ ...prevErrors, userGuid: '', picGuid: '' }));
    };

    const handleRemotePilotChange = (picGuid) => {
        const selectedPerson = personnelData.find((person) => person.userGuid === picGuid);

        setIsDirty(true);

        // Update missionData via the parent's handleFieldChange
        handleFieldChange({ target: { name: 'picGuid', value: picGuid } });
        handleFieldChange({
            target: {
                name: 'remotePic',
                value: selectedPerson ? `${selectedPerson.firstName} ${selectedPerson.lastName}` : ''
            }
        }
        );

        setErrors((prevErrors) => ({ ...prevErrors, picGuid: '' }));
    };

    const updateMissionField = (fieldName, fieldValue) => {
        setIsDirty(true);

        // Update missionData
        setMissionData((prevData) => ({
            ...prevData,
            [fieldName]: fieldValue,
        }));

        // Call the parent's handleFieldChange
        handleFieldChange({ target: { name: fieldName, value: fieldValue } });
    };

    const handleCrewToggle = (userGuid) => {
        const currentCrew = missionData.crew?.additionalCrew || [];
        const updatedCrew = currentCrew.some((member) => member.userGuid === userGuid)
            ? currentCrew.filter((member) => member.userGuid !== userGuid)
            : [...currentCrew, { name: '', userGuid, role: 'Additional Crew', certification: '' }];

        // Use setCrew to manage the crew value
        setCrew(updatedCrew);
    };

    const handleSkillFilterChange = (skills) => {
        setIsDirty(true);

        if (skills.includes('_allSkills') && skills.length > 1) {
            skills = skills.filter((skill) => skill !== '_allSkills');
        }

        if (skills.includes('_allSkills')) {
            setSelectedSkills([]); // Clear other selections
            setDropdownOpen(false);
        } else {
            setSelectedSkills(skills);
        }

        // Clear crew when the filter changes
        setCrew([]);
    };

    if (loading) {
        {/*
        return (
            <Box sx={{ padding: 2 }}>
                <LinearProgress />
            </Box>
        );
        */}
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #dcdcdc',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
        >
            {/* Section Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: isExpanded ? '0px' : '0px',
                    cursor: 'pointer',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <PeopleIcon sx={{ color: '#42a5f5', marginRight: '8px' }} />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Personnel
                </Typography>
                <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={isExpanded}>
                <Box
                    sx={{
                        display: 'flex', // Enable flexbox
                        gap: '16px', // Space between the text fields
                        marginBottom: '16px', // Add spacing below the row
                    }}
                >
                    {/* Assigned User Selector */}
                    <TextField
                        select
                        label="Assigned to"
                        value={assignedToValue} //{missionData?.userGuid || ''}
                        onChange={(e) => handleAssignedUserChange(e.target.value)}
                        fullWidth
                        sx={{
                            marginTop: '24px',
                            marginBottom: '16px',
                        }}
                        slotProps={{
                            inputLabel: {
                                sx: {
                                    //transform: 'translate(14px, 12px) scale(1)', // Adjust for vertical centering
                                },
                            },
                            inputLable: {
                                sx: {
                                    //height: '44px', // Match the input height
                                    display: 'flex',
                                    alignItems: 'center', // Ensure content is vertically centered
                                },
                            },
                        }}

                        error={!!errors.userGuid}
                        helperText={errors.userGuid}
                    >
                        <MenuItem value="" disabled>
                            Select assigned to user
                        </MenuItem>
                        {personnelData.length === 0 ? (
                            <MenuItem value="" disabled>
                                No personnel available
                            </MenuItem>
                        ) : (
                            personnelData.map((person) => (
                                <MenuItem key={person.userGuid} value={person.userGuid}>
                                    {person.firstName} {person.lastName}
                                </MenuItem>
                            ))
                        )}
                    </TextField>

                    {/* Remote PIC Selector */}

                    <TextField
                        select
                        label="Remote PIC"
                        value={remotePicValue} //{missionData?.picGuid || ''}
                        onChange={(e) => handleRemotePilotChange(e.target.value)}
                        fullWidth
                        sx={{
                            marginTop: '24px',
                            marginBottom: '16px',
                            //'& .MuiInputBase-root': {
                            //    height: '44px',
                            //},
                        }}
                        error={!!errors.picGuid}
                        helperText={errors.picGuid}
                    >
                        <MenuItem value="" disabled>
                            Select Remote PIC
                        </MenuItem>
                        {remotePicEligiblePersonnel.map((person) => (
                            <MenuItem key={person.userGuid} value={person.userGuid}>
                                {person.firstName} {person.lastName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {hasCapability(subscription?.planId, CAPABILITIES.SKILLS) ? (
                    <>
                        <Typography variant="body2" sx={{ marginTop: '8px', marginBottom: '16px', fontWeight: '500' }}>
                            Select additional crew members
                        </Typography>

                        {/* Skill Filter */}
                        <FormControl fullWidth sx={{ marginBottom: '0px' }}>
                            <InputLabel id="filter-by-skills-label">Filter by Skills</InputLabel>
                            <Select
                                label="Filter by Skills"
                                labelId="filter-by-skills-label"
                                multiple
                                value={selectedSkills.length === 0 ? ['_allSkills'] : selectedSkills}
                                onChange={(e) => {
                                    let selected = e.target.value;

                                    // User selected All Skills
                                    if (selected.includes('_allSkills') && selected.indexOf('_allSkills') > 0) {
                                        selected = ['_allSkills'];
                                    }

                                    // After checking for user selected All Skills, _allSkills should only be at index 0
                                    if (selected.length > 1) {
                                        selected = selected.filter((selected) => selected !== '_allSkills');
                                    }

                                    if (selected.includes('_allSkills')) {
                                        // If '_allSkills' is selected along with other skills, keep only '_allSkills'
                                        selected = ['_allSkills'];
                                        setSelectedSkills(selected);
                                        //console.log('All Skills selected. Deselecting other skills.');

                                        updateMissionField('crew', []);

                                        //setMissionData((prevData) => ({
                                        //    ...prevData,
                                        //    crew: [], // Clear selected personnel
                                        //}));

                                        // Do NOT clear crew
                                        setDropdownOpen(false); // Close the dropdown
                                    } else {
                                        //selected = selected.filter((selected) => selected !== '_allSkills');
                                        setSelectedSkills(selected);
                                        //console.log('Updated selected skills:', selected);

                                        // Clear crew
                                        updateMissionField('crew', []);
                                        //setMissionData((prevData) => ({
                                        //    ...prevData,
                                        //    crew: [], // Clear selected personnel
                                        //}));
                                        setDropdownOpen(true); // Keep the dropdown open
                                    }
                                }}
                                onOpen={() => setDropdownOpen(true)} // Open the dropdown
                                onClose={() => setDropdownOpen(false)} // Close the dropdown
                                open={dropdownOpen} // Control the open state
                                renderValue={(selected) => {
                                    //console.log('renderValue selected:', selected);

                                    if (selected.includes('_allSkills') || selected.length === 0) {
                                        return <em>All Skills</em>; // Display italicized "All Skills"
                                    }

                                    return selected.map((skill) => getSkillLabel(skill)).join(', '); // Display selected skills
                                }}
                                //fullWidth
                                sx={{
                                    width: 'calc(50% - 8px)',
                                    marginBottom: '16px',
                                }}
                            >
                                <MenuItem
                                    value="_allSkills" // Use a unique value for "All Skills"
                                >
                                    <em>All Skills</em>
                                </MenuItem>
                                {allSkills.map((skillKey) => (
                                    <MenuItem key={skillKey} value={skillKey}>
                                        {getSkillLabel(skillKey)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Crew List */}

                        <List>
                            {skillsDropdownPersonnel
                                .filter((person) => {
                                    // If no skills are selected, show all personnel
                                    if (selectedSkills.includes('_allSkills') || selectedSkills.length === 0) {
                                        return true;
                                    }

                                    // Check if any of the user's skills match the selected skills
                                    const personSkills = Object.values(person.skills || {}).flat();
                                    return selectedSkills.some((skill) => personSkills.includes(skill));
                                })
                                .map((person) => (
                                    <ListItem
                                        key={person.userGuid}
                                        sx={{
                                            borderBottom: '1px solid #dcdcdc',
                                            padding: '0px 0px',
                                            transition: 'background-color 0.2s',
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={(missionData.crew?.additionalCrew || []).some((member) => member.userGuid === person.userGuid)}
                                            onChange={() => handleCrewToggle(person.userGuid)}
                                            sx={{
                                                marginRight: '8px',
                                            }}
                                        />
                                        <ListItemText
                                            primary={`${person.firstName} ${person.lastName}`}
                                            secondary={
                                                Array.isArray(Object.values(person.skills || {}).flat())
                                                    ? Object.values(person.skills || {})
                                                        .flat()
                                                        .map((skill) => getSkillLabel(skill).trim())
                                                        .join(', ')
                                                    : ''
                                            }
                                        />
                                    </ListItem>
                                ))}
                        </List>
                    </>
                ) : null}
            </Collapse>
        </Box>
    );
});

export default PersonnelSection;